/**
 * S-01 ホーム（スタッフ）。当日の入力状況・今月の状況・提出状況・直近の日報。
 */

import { Link } from 'react-router-dom';
import { endpoints } from '../../api/endpoints.js';
import { useMe } from '../../auth/context.js';
import {
  Card,
  ErrorMessage,
  Kpi,
  Loading,
  Notice,
  ProvisionalBadge,
  StatusPill,
} from '../../components/ui.js';
import {
  currentMonth,
  formatDateLabel,
  formatDuration,
  formatMonthLabel,
  shiftMonth,
  todayIso,
} from '../../lib/format.js';
import { useAsync } from '../../lib/useAsync.js';

export function HomePage(): React.JSX.Element {
  const me = useMe();
  const month = currentMonth();
  const today = todayIso();

  const state = useAsync(async () => {
    const [thisMonth, lastMonth] = await Promise.all([
      endpoints.summary(month),
      endpoints.summary(shiftMonth(month, -1)).catch(() => null),
    ]);
    const reports = await endpoints.monthlyReports(month);
    return { thisMonth, lastMonth, reports };
  }, [month]);

  if (state.loading && !state.data) return <Loading />;
  if (state.error) return <ErrorMessage error={state.error} />;
  if (!state.data) return <Loading />;

  const { thisMonth, lastMonth, reports } = state.data;
  const todayRow = reports.days.find((day) => day.date === today);
  const entered = reports.days.filter((day) => day.report !== null);
  const submitted = entered.filter((day) =>
    ['submitted', 'approved'].includes(day.report?.status ?? ''),
  ).length;
  const drafts = entered.filter((day) => day.report?.status === 'draft').length;
  const rejected = entered.filter((day) => day.report?.status === 'rejected');
  const workdaysSoFar = reports.days.filter(
    (day) => day.dayType === '平日' && day.date <= today,
  ).length;
  const recent = [...entered].reverse().slice(0, 5);

  const delta = (current: number, previous: number | undefined): string => {
    if (previous === undefined) return '前月の記録なし';
    const diff = current - previous;
    if (diff === 0) return '前月と同じ';
    return `前月比 ${diff > 0 ? '+' : '−'}${formatDuration(Math.abs(diff))}`;
  };

  return (
    <>
      <div>
        <h1 className="page-heading">こんにちは、{me.employee.name}さん</h1>
        <p className="text-sm text-ink-muted">
          {formatDateLabel(today)}｜{me.employee.department}
          {me.approvalGroup && `・${me.approvalGroup.name}`}
        </p>
      </div>

      {rejected.length > 0 && (
        <Notice tone="warn" title={`差戻しの日報が${rejected.length}件あります`}>
          <ul className="list-disc pl-5">
            {rejected.map((day) => (
              <li key={day.date}>
                <Link className="underline" to={`/daily/${day.date}`}>
                  {formatDateLabel(day.date)}
                </Link>
                ：{day.report?.rejectionReason || '理由の記載はありません'}
              </li>
            ))}
          </ul>
        </Notice>
      )}

      <Card
        title="今日の日報"
        action={
          <Link className="btn btn--primary btn--sm" to={`/daily/${today}`}>
            1日ずつ入力
          </Link>
        }
      >
        {todayRow?.report ? (
          <div className="flex flex-wrap items-center gap-3">
            <StatusPill status={todayRow.report.status} locked={todayRow.report.locked} />
            <span className="text-sm">
              稼働 {formatDuration(todayRow.computed?.totalMinutes ?? 0)}／実働{' '}
              {formatDuration(todayRow.computed?.workMinutes ?? 0)}
            </span>
            <span className="text-sm text-ink-muted">
              {todayRow.report.taskNote || '業務内容は未記入です'}
            </span>
          </div>
        ) : (
          <p className="text-sm">
            今日の日報がまだ入力されていません。
            <Link className="ml-1 font-semibold text-brand-700 underline" to={`/daily/${today}`}>
              入力する
            </Link>
          </p>
        )}
      </Card>

      <Card
        title={
          <span className="flex items-center gap-2">
            {formatMonthLabel(month)}の状況 {me.provisional && <ProvisionalBadge />}
          </span>
        }
        description="みなし補填を含む集計です。給与確定額ではありません。"
        action={
          <Link className="btn btn--secondary btn--sm" to="/monthly-summary">
            月次集計を見る
          </Link>
        }
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi
            label="実働時間"
            value={thisMonth.summary.display.workHours}
            note={delta(thisMonth.summary.workMinutes, lastMonth?.summary.workMinutes)}
          />
          <Kpi
            label="稼働時間"
            value={thisMonth.summary.display.totalHours}
            note={delta(thisMonth.summary.totalMinutes, lastMonth?.summary.totalMinutes)}
          />
          <Kpi
            label="残業時間"
            value={thisMonth.summary.display.monthlyOvertimeHours}
            note="日残業＋週超過"
          />
          <Kpi
            label="出勤日数"
            value={`${thisMonth.summary.attendanceDays}日`}
            note={`休出 ${thisMonth.summary.holidayWorkDays}日`}
          />
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="提出状況">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="kpi-value">{submitted}</p>
              <p className="text-[0.8rem] text-ink-muted">提出済み</p>
            </div>
            <div>
              <p className="kpi-value">{drafts}</p>
              <p className="text-[0.8rem] text-ink-muted">下書き</p>
            </div>
            <div>
              <p className="kpi-value">{Math.max(0, workdaysSoFar - entered.length)}</p>
              <p className="text-[0.8rem] text-ink-muted">未入力</p>
            </div>
          </div>
          <p className="mt-3 text-[0.78rem] text-ink-muted">
            今日までの平日 {workdaysSoFar}日が対象です。休日は勤務した日だけ入力します。
          </p>
        </Card>

        <Card
          title="直近の日報"
          action={
            <Link className="btn btn--secondary btn--sm" to="/monthly-list">
              月間一覧を見る
            </Link>
          }
        >
          {recent.length === 0 ? (
            <p className="text-sm text-ink-muted">まだ入力がありません。</p>
          ) : (
            <ul className="divide-y divide-line text-sm">
              {recent.map((day) => (
                <li key={day.date} className="flex items-center justify-between gap-3 py-2">
                  <Link className="font-semibold hover:underline" to={`/daily/${day.date}`}>
                    {formatDateLabel(day.date)}
                  </Link>
                  <span className="flex-1 truncate text-ink-muted">
                    {day.report?.taskNote || '—'}
                  </span>
                  <span className="tabular">{formatDuration(day.computed?.totalMinutes ?? 0)}</span>
                  <StatusPill status={day.report?.status ?? null} locked={day.report?.locked} />
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}
